import React, { useState } from "react";
import { Button, ListItemText, Menu, MenuItem, Radio } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { config } from "../config.js";
import { useUserDataContext } from "../Contexts/userdataContext";
import { isAnyAdmin, isDistributor } from "../Utilities/UserDataFunctions";

//OB_IssueType: 1 = Order Change Request, 2 = Ticket, 3 = On-Top Request
const TICKET_ISSUE_TYPE_ID = 2;

const url = `changerequesttypes/${TICKET_ISSUE_TYPE_ID}`;

//On Top is not a ticket type - it is hardcoded in OBO's flyout under a different issue type and
//navigates to the on-top request page instead of opening the ticket popup.
const ON_TOP = "onTop";

export default function TicketRequestMenu() {
    const { userData } = useUserDataContext();
    const [anchorEl, setAnchorEl] = useState(null);
    const [ticketTypes, setTicketTypes] = useState(null);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState("");

    const open = Boolean(anchorEl);
    const canRequestOnTop = isAnyAdmin(userData) || isDistributor(userData);

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);

        //the list is small and rarely changes, so fetch it once on first open rather than on every
        //page load - most users never open this menu
        if (ticketTypes === null) {
            //clear any earlier failure so a successful retry is not masked by the stale message
            setError("");
            fetch(url)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error(`Request failed with status ${res.status}`);
                    }
                    return res.json();
                })
                .then((data) => {
                    setTicketTypes(data);
                    setIsReady(true);
                })
                .catch(() => {
                    setError("Ticket types could not be loaded.");
                    setIsReady(true);
                });
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // OBO opens the matching ticket popup on any page.master page that receives NewTicketType.
    // Mirrors OBO's own radio list: one type at a time, and picking one goes straight there.
    const handleSelect = (ticketTypeId) => {
        setAnchorEl(null);
        window.location.assign(`${config.OBO_URL}/default.aspx?NewTicketType=${ticketTypeId}`);
    };

    // On Top has no popup - OBO's rbOnTop redirects straight to the on-top request page.
    const handleSelectOnTop = () => {
        setAnchorEl(null);
        window.location.assign(`${config.OBO_URL}/OrderChangeRequest/change_request.aspx`);
    };

    const renderMenuItems = () => {
        if (!isReady) {
            return <MenuItem disabled>Loading&hellip;</MenuItem>;
        }

        if (error) {
            return <MenuItem disabled>{error}</MenuItem>;
        }

        if (!ticketTypes || ticketTypes.length === 0) {
            return <MenuItem disabled>No ticket types available</MenuItem>;
        }

        return ticketTypes.map((ticketType) => (
            <MenuItem key={ticketType.id} onClick={() => handleSelect(ticketType.id)}>
                <Radio
                    size="small"
                    checked={false}
                    value={ticketType.id}
                    name="ticket-request-type"
                    tabIndex={-1}
                    disableRipple
                    sx={{ py: 0, pl: 0, pr: 1 }}
                />
                <ListItemText primary={ticketType.description} />
            </MenuItem>
        ));
    };

    return (
        <React.Fragment>
            <Button
                variant="outlined"
                onClick={handleOpen}
                endIcon={<ArrowDropDownIcon />}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
            >
                Ticket Request
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {renderMenuItems()}
                {/*rendered outside renderMenuItems so a failed ticket type fetch cannot hide it,
                   matching OBO where the two are independent*/}
                {canRequestOnTop && (
                    <MenuItem onClick={handleSelectOnTop}>
                        <Radio
                            size="small"
                            checked={false}
                            value={ON_TOP}
                            name="ticket-request-type"
                            tabIndex={-1}
                            disableRipple
                            sx={{ py: 0, pl: 0, pr: 1 }}
                        />
                        <ListItemText primary="On Top" />
                    </MenuItem>
                )}
            </Menu>
        </React.Fragment>
    );
}
